import { 
  Directive, 
  Input, 
  ElementRef, 
  Renderer2, 
  HostListener,
  OnDestroy,
  Injector,
  EnvironmentInjector,
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef
} from '@angular/core';
import { TooltipComponent } from './tooltip.component';

@Directive({
  selector: '[appTooltip]',
  standalone: true
})
export class TooltipDirective implements OnDestroy {
  @Input('appTooltip') tooltipText = '';
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
  @Input() tooltipDelay = 0;

  private tooltipComponent: ComponentRef<TooltipComponent> | null = null;
  private showTimeoutId: any;
  private hideTimeoutId: any;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private environmentInjector: EnvironmentInjector,
    private appRef: ApplicationRef
  ) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }

    this.showTimeoutId = setTimeout(() => {
      this.showTooltip();
    }, this.tooltipDelay);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }

    this.hideTimeoutId = setTimeout(() => {
      this.hideTooltip();
    }, 100);
  }

  @HostListener('click')
  onClick(): void {
    this.hideTooltip();
  }

  private showTooltip(): void {
    if (this.tooltipComponent || !this.tooltipText.trim()) {
      return;
    }

    // Create the tooltip component
    this.tooltipComponent = createComponent(TooltipComponent, {
      environmentInjector: this.environmentInjector
    });

    // Set the tooltip text and position
    this.tooltipComponent.instance.text = this.tooltipText;
    this.tooltipComponent.instance.position = this.tooltipPosition;

    // Attach to the application
    this.appRef.attachView(this.tooltipComponent.hostView);

    // Get the DOM element and append to body
    const domElem = (this.tooltipComponent.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    // Position the tooltip
    this.positionTooltip(domElem);

    // Show with animation
    setTimeout(() => {
      this.tooltipComponent?.instance.show();
    }, 10);
  }

  private hideTooltip(): void {
    if (this.tooltipComponent) {
      this.tooltipComponent.instance.hide();
      
      setTimeout(() => {
        if (this.tooltipComponent) {
          this.appRef.detachView(this.tooltipComponent.hostView);
          this.tooltipComponent.destroy();
          this.tooltipComponent = null;
        }
      }, 200);
    }
  }

  private positionTooltip(tooltipElement: HTMLElement): void {
    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    let top: number;
    let left: number;

    switch (this.tooltipPosition) {
      case 'top':
        top = hostRect.top + scrollTop - tooltipRect.height - 8;
        left = hostRect.left + scrollLeft + (hostRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'bottom':
        top = hostRect.bottom + scrollTop + 8;
        left = hostRect.left + scrollLeft + (hostRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'left':
        top = hostRect.top + scrollTop + (hostRect.height / 2) - (tooltipRect.height / 2);
        left = hostRect.left + scrollLeft - tooltipRect.width - 8;
        break;
      case 'right':
        top = hostRect.top + scrollTop + 9;
        left = hostRect.left + scrollLeft + 80;
        break;
      default:
        top = hostRect.top + scrollTop - tooltipRect.height - 8;
        left = hostRect.left + scrollLeft + (hostRect.width / 2) - (tooltipRect.width / 2);
    }

    // Ensure tooltip stays within viewport
    const padding = 8;
    if (left < padding) {
      left = padding;
    } else if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }

    if (top < padding) {
      top = hostRect.bottom + scrollTop + 8; // Flip to bottom
    }

    this.renderer.setStyle(tooltipElement, 'position', 'absolute');
    this.renderer.setStyle(tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(tooltipElement, 'left', `${left}px`);
    this.renderer.setStyle(tooltipElement, 'z-index', '9999');
  }

  ngOnDestroy(): void {
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
    }
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
    }
    this.hideTooltip();
  }
}
